
import { useState, useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import { supabase } from "@/integrations/supabase/client";

export const usePremiumAccess = () => {
  const { user } = useAuth();
  const [hasAccess, setHasAccess] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [expiry, setExpiry] = useState<string | null>(null);
  const [purchaseDate, setPurchaseDate] = useState<string | null>(null);
  const [daysLeft, setDaysLeft] = useState<number | null>(null);

  const fetchedOnceRef = useRef(false);
  const lastUserIdRef = useRef<string | null>(null);

  const fetchAccess = async () => {
    if (!user) {
      setHasAccess(false);
      setExpiry(null);
      setPurchaseDate(null);
      setDaysLeft(null);
      return;
    }

    if (fetchedOnceRef.current) return;
    setIsChecking(true);

    try {
      // First check the is_premium status from profiles table
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('is_premium')
        .eq('id', user.id)
        .single();

      // If user is marked as premium in profiles, they have access regardless of payment status
      if (profileData?.is_premium) {
        // Get purchase data for display purposes
        const { data: purchaseData } = await supabase
          .from('printables_purchases')
          .select('start_date, end_date')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        const purchase = purchaseData?.[0];
        setHasAccess(true);
        setPurchaseDate(purchase?.start_date || null);
        setExpiry(purchase?.end_date || null);
        
        if (purchase?.end_date) {
          const endDate = new Date(purchase.end_date);
          const now = new Date();
          if (endDate > now) {
            const daysRemaining = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            setDaysLeft(daysRemaining);
          } else {
            setDaysLeft(null);
          }
        } else {
          setDaysLeft(null);
        }
      } else {
        // Check payment-based access
        const { data, error } = await supabase
          .from('printables_purchases')
          .select('start_date, end_date, razorpay_order_id, razorpay_payment_id, razorpay_signature, status')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        if (error || !data || data.length === 0) {
          setHasAccess(false);
          setPurchaseDate(null);
          setExpiry(null);
          setDaysLeft(null);
        } else {
          const purchase = data[0];
          const start = purchase.start_date;
          const end = purchase.end_date;
          
          // Determine if premium access should be granted based on payment completion and expiry
          let isPremium = false;
          
          if (end) {
            const endDate = new Date(end);
            const now = new Date();
            
            // For Razorpay payments, check if payment is completed
            if (purchase.razorpay_order_id) {
              isPremium = !!(purchase.razorpay_payment_id && purchase.razorpay_signature && endDate > now);
            } else {
              // For admin payments, check status and expiry
              isPremium = purchase.status === 'completed' && endDate > now;
            }
          }
          
          setHasAccess(isPremium);
          setPurchaseDate(start);
          setExpiry(end);
          
          if (end && isPremium) {
            const daysRemaining = Math.ceil((new Date(end).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            setDaysLeft(daysRemaining);
          } else {
            setDaysLeft(null);
          }
        }
      }

      fetchedOnceRef.current = true;
    } catch (err) {
      console.error("Error checking premium access", err);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    const currentId = user?.id ?? null;
    // Reset cached fetch flag whenever the auth identity changes
    if (currentId !== lastUserIdRef.current) {
      fetchedOnceRef.current = false;
      lastUserIdRef.current = currentId;
    }
    if (user) {
      fetchAccess();
    } else {
      setHasAccess(false);
      setExpiry(null);
      setPurchaseDate(null);
      setDaysLeft(null);
      fetchedOnceRef.current = false;
    }
  }, [user?.id]);

  const refreshStatus = async () => {
    fetchedOnceRef.current = false;
    await fetchAccess();
  };

  return {
    hasAccess,
    isChecking,
    purchaseDate,
    expiry,
    daysLeft,
    refreshStatus,
    setHasAccess,
  };
};
